import { cn } from '@shared/utils/cn';

export type StatusVariant =
  | 'pending'
  | 'reviewing'
  | 'approved'
  | 'rejected'
  | 'paid'
  | 'upcoming'
  | 'overdue'
  | 'free'
  | 'active'
  | 'cancelled'
  | 'new'
  | 'neutral';

const variantStyles: Record<StatusVariant, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  reviewing: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  upcoming: 'bg-sky-50 text-sky-700 border-sky-200',
  overdue: 'bg-rose-50 text-rose-700 border-rose-200',
  free: 'bg-teal-50 text-teal-700 border-teal-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  new: 'bg-emerald-500 text-white border-emerald-500',
  neutral: 'bg-muted text-muted-foreground border-border',
};

interface StatusBadgeProps {
  variant: StatusVariant;
  children: React.ReactNode;
  className?: string;
}

export function StatusBadge({ variant, children, className }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        variantStyles[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Mapea estados crudos del backend a una variante visual.
 * Claims/subscriptions del API usan strings como APPROVED/PAID/PENDING/etc.
 */
export function variantForStatus(raw: string | undefined | null): StatusVariant {
  const v = (raw ?? '').toUpperCase();
  if (v === 'APPROVED') return 'approved';
  if (v === 'PAID') return 'paid';
  if (v === 'REJECTED') return 'rejected';
  if (v === 'PENDING') return 'pending';
  if (v === 'REVIEWING' || v === 'IN_REVIEW') return 'reviewing';
  if (v === 'ACTIVE') return 'active';
  if (v === 'PAST_DUE' || v === 'OVERDUE') return 'overdue';
  if (v === 'UPCOMING' || v === 'SCHEDULED') return 'upcoming';
  if (v === 'FREE') return 'free';
  if (v === 'CANCELLED' || v === 'CANCELED') return 'cancelled';
  return 'neutral';
}

/** Etiqueta en español para mostrar al usuario. */
export function labelForStatus(raw: string | undefined | null): string {
  const v = (raw ?? '').toUpperCase();
  const map: Record<string, string> = {
    PENDING: 'En revisión',
    REVIEWING: 'En revisión',
    IN_REVIEW: 'En revisión',
    APPROVED: 'Aprobado',
    PAID: 'Pagado',
    REJECTED: 'Rechazado',
    ACTIVE: 'Activo',
    PAST_DUE: 'Vencido',
    OVERDUE: 'Vencido',
    UPCOMING: 'Próximo',
    SCHEDULED: 'Programado',
    FREE: 'Gratis',
    CANCELLED: 'Cancelado',
    CANCELED: 'Cancelado',
    SUSPENDED: 'Suspendido',
  };
  return map[v] ?? (raw ?? '');
}
